<template>
    <div class="jobs-page">
        <!-- Page header -->
        <div class="jobs-header">
            <div class="jobs-header-inner">
                <img src="@/assets/images/rosman-market-new.png" class="jobs-logo-img" alt="רוסמן" />
                <div class="jobs-header-divider"></div>
                <div>
                    <h1 class="jobs-page-title">משרות פנויות</h1>
                    <p class="jobs-page-subtitle">הצטרף לצוות רוסמן</p>
                </div>
            </div>
        </div>

        <!-- Search bar -->
        <div class="jobs-search-wrap">
            <div class="jobs-search-box">
                <i class="material-icons search-icon">search</i>
                <input
                    v-model="search"
                    type="text"
                    placeholder="חיפוש לפי תפקיד או סניף..."
                    class="jobs-search-input"
                />
                <button v-if="search" class="search-clear-btn" @click="search = ''">✕</button>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="jobs-loading">
            <span class="spinner"></span>
            <span>טוען משרות...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!filteredAds.length" class="jobs-empty">
            <i class="material-icons">search_off</i>
            <p v-if="search">לא נמצאו תוצאות עבור "{{ search }}"</p>
            <p v-else>אין משרות פנויות כרגע. חזור שוב בקרוב!</p>
        </div>

        <!-- Grid of ads -->
        <div v-else class="jobs-grid">
            <AdCard
                v-for="ad in filteredAds"
                :key="ad.id"
                :ad="ad"
                @apply="openApply(ad)"
            />
        </div>

        <!-- Apply form modal -->
        <ApplyForm
            v-if="selectedAd"
            :ad="selectedAd"
            @close="selectedAd = null"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { projectFirestore } from '@/firebase/config'
import { slide_pop_error } from '@/Methods/Msgs'
import AdCard from '@/components/Drushim/AdCard.vue'
import ApplyForm from '@/components/Drushim/ApplyForm.vue'

export default {
    name: 'Jobs',
    components: { AdCard, ApplyForm },
    setup() {
        const route = useRoute()
        const ads = ref([])
        const search = ref('')
        const isLoading = ref(false)
        const selectedAd = ref(null)

        const filteredAds = computed(() => {
            if (!search.value) return ads.value
            const q = search.value.toLowerCase()
            return ads.value.filter(ad =>
                (ad.title && ad.title.toLowerCase().includes(q)) ||
                (ad.branch && ad.branch.toLowerCase().includes(q))
            )
        })

        const openApply = (ad) => {
            selectedAd.value = ad
        }

        onMounted(async () => {
            isLoading.value = true
            try {
                const snap = await projectFirestore
                    .collection('JobAds')
                    .where('active', '==', true)
                    .get()
                ads.value = snap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => {
                        const aTime = a.createdAt?.toDate?.() ?? new Date(a.createdAt ?? 0)
                        const bTime = b.createdAt?.toDate?.() ?? new Date(b.createdAt ?? 0)
                        return bTime - aTime
                    })

                // Auto-open ApplyForm if ?ad=<id> query param is present
                const adId = route.query.ad
                if (adId) {
                    let targetAd = ads.value.find(a => a.id === adId)
                    if (!targetAd) {
                        // Ad might exist but not be in the active list — fetch directly.
                        // Paused/deleted ads are not opened: the submit function rejects them anyway.
                        try {
                            const doc = await projectFirestore.collection('JobAds').doc(adId).get()
                            if (doc.exists && doc.data().active === true) {
                                targetAd = { id: doc.id, ...doc.data() }
                            } else {
                                slide_pop_error('המשרה אינה זמינה יותר')
                            }
                        } catch { /* silent */ }
                    }
                    if (targetAd) {
                        selectedAd.value = targetAd
                    }
                }
            } catch (err) {
                console.error('JobAds fetch error:', err)
            } finally {
                isLoading.value = false
            }
        })

        return { ads, search, isLoading, selectedAd, filteredAds, openApply }
    }
}
</script>

<style scoped>
.jobs-page {
    min-height: 100vh;
    background: #F5F4EF;
    direction: rtl;
    font-family: inherit;
}

/* Header */
.jobs-header {
    background: #FEF200;
    border-bottom: 4px solid #000;
    padding: 36px 24px 32px;
}
.jobs-header-inner {
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 24px;
    color: #000;
}
.jobs-logo-img {
    height: 64px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
}
.jobs-header-divider {
    width: 3px;
    height: 52px;
    background: rgba(0, 0, 0, 0.25);
    border-radius: 2px;
    flex-shrink: 0;
}
.jobs-page-title {
    font-size: 34px;
    font-weight: 800;
    margin: 0 0 4px;
    color: #000;
    letter-spacing: -0.5px;
}
.jobs-page-subtitle {
    font-size: 16px;
    color: #000;
    opacity: 0.7;
    margin: 0;
    font-weight: 500;
}

/* Search */
.jobs-search-wrap {
    max-width: 1100px;
    margin: -22px auto 24px;
    padding: 0 24px;
}
.jobs-search-box {
    background: #fff;
    border-radius: 50px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: center;
    padding: 0 18px;
    gap: 10px;
    height: 52px;
    border: 1.5px solid #e0e0e0;
}
.search-icon {
    color: #aaa;
    font-size: 22px;
    flex-shrink: 0;
}
.jobs-search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 15px;
    color: #333;
    background: transparent;
    font-family: inherit;
}
.jobs-search-input::placeholder {
    color: #bbb;
}
.search-clear-btn {
    background: #f0f0f0;
    border: none;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    cursor: pointer;
    font-size: 13px;
    color: #888;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}
.search-clear-btn:hover {
    background: #e0e0e0;
}

/* Loading / Empty */
.jobs-loading,
.jobs-empty {
    max-width: 1100px;
    margin: 0 auto;
    padding: 80px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #aaa;
    text-align: center;
}
.jobs-loading {
    flex-direction: row;
    gap: 12px;
}
.jobs-empty i {
    font-size: 72px;
    color: #ccc;
}
.jobs-empty p {
    font-size: 16px;
    color: #888;
    max-width: 340px;
}

/* Grid */
.jobs-grid {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px 60px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
}

@media (max-width: 600px) {
    .jobs-page-title {
        font-size: 24px;
    }
    .jobs-grid {
        padding: 0 16px 40px;
        gap: 16px;
    }
    .jobs-search-wrap {
        padding: 0 16px;
    }
}

/* Spinner */
.spinner {
    width: 26px;
    height: 26px;
    border: 3px solid #ddd;
    border-top-color: #1579F2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
